// This file is largely a copy-paste of webapp/src/Components/UI/Notifications/IconButton.jsx.
// The difference is the icon is an FDIcon
import React, { useContext, useEffect, useRef } from 'react';
import ClickAwayListener from '@material-ui/core/ClickAwayListener';
import MuiIconButton from '@material-ui/core/IconButton';
import { useHistory } from 'react-router-dom';
import AppContext from 'Context/AppContext';
import { useWindowContext } from 'Context/WindowContext';
import useEventTrackingLogEvent from 'EventTracking/useEventTrackingLogEvent';
import useUserContext from 'Context/UserContext';
import Badge from 'Components/ComponentLibrary/ExternalLibraries/Libraries/MaterialV4/Badge';
import PreviewPopover from 'Components/UI/Notifications/PreviewPopover';
import { buildUseStyles } from 'Components/ComponentLibrary/ExternalLibraries/Libraries/MaterialV4/styles';
import { FDIcon } from '@facet-wealth/design';
import fr from 'facet-rest';

const NOTIFICATION_SYNC_INTERVAL = 60000;

const useStyles = buildUseStyles('Notifications', () => ({
  badge: {
    fontSize: '10px',
    height: '16px',
    minWidth: '16px',
    padding: '0 4px',
    right: '25%',
    bottom: '25%',
  },
}));

const initNotificationState = {
  unreadPollingNotificationCount: 0,
  pollingNotifications: [],
  syncSuccessful: true,
};

const NotificationsNavButtonContent = () => {
  const classes = useStyles();
  const [notificationState, setState] = React.useState(initNotificationState);
  const history = useHistory();
  const intervalRef = useRef();
  const { user } = useUserContext();
  const { windowIsVisible } = useWindowContext();
  const { logEvent } = useEventTrackingLogEvent();

  const {
    markNotificationRead,
    markNotificationsRead,
    fetchNotifications,
  } = useContext(AppContext);

  const handlePolling = () => fetchNotifications()
    .then(groups => setState({
      ...notificationState,
      unreadPollingNotificationCount: groups.unreadNotificationCount,
      pollingNotifications: Object.values(groups.notifications),
      syncSuccessful: true,
    }))
    .catch(() => {
      setState({ ...initNotificationState, syncSuccessful: false });
    });

  const markAllPollingNotificationsRead = () => markNotificationsRead().then(groups => setState({
    ...notificationState,
    unreadPollingNotificationCount: groups.unreadNotificationCount,
    pollingNotifications: Object.values(groups.notifications),
    syncSuccessful: true,
  }));

  const markPollingNotificationRead = id => markNotificationRead(id)
    .then(handlePolling)
    .catch(() => {
      setState({ ...notificationState, syncSuccessful: false });
    });

  const shouldClear = location => location.pathname === '/login'
      || location.pathname === '/forgot-password'
      || location.pathname === '/change-password'
      || location.pathname === '/';

  useEffect(() => {
    if (user && user.id > 0) {
      handlePolling();
    }
    if (windowIsVisible && intervalRef.current === undefined) {
      setTimeout(() => handlePolling(), NOTIFICATION_SYNC_INTERVAL);
      intervalRef.current = setInterval(() => {
        if (windowIsVisible) {
          handlePolling();
        }
      }, NOTIFICATION_SYNC_INTERVAL);
    }
    return () => {
      // If we're changing from windowIsVisible to not visible or we shouldClear
      if (windowIsVisible || (shouldClear(history.location) && intervalRef.current)) {
        clearInterval(intervalRef.current);
        intervalRef.current = undefined;
      }
    };
  }, [user, windowIsVisible]);
  const {
    pollingNotifications,
    unreadPollingNotificationCount,
    syncSuccessful,
  } = notificationState;
  const [anchorEl, setAnchorEl] = React.useState(null);

  const badgeProps = syncSuccessful
    ? {
      badgeContent: `${unreadPollingNotificationCount}`,
      anchorOrigin: { vertical: 'bottom', horizontal: 'right' },
      invisible: unreadPollingNotificationCount === 0,
      classes: { badge: classes.badge },
      overlap: 'circle',
    }
    : {
      badgeContent: '!',
      classes: { badge: classes.badge },
      overlap: 'circle',
    };

  const isDisabled = !user;
  const isOpen = !!anchorEl && !!user;
  let userTypes;

  // eslint-disable-next-line prefer-destructuring
  if (user) userTypes = user.userTypes;

  return (
    <Badge {...badgeProps}>
      <ClickAwayListener onClickAway={() => setAnchorEl(null)}>
        <MuiIconButton
          id="header-notifications-button"
          test="header-notifications-button"
          role="presentation"
          component="span"
          disabled={isDisabled}
          onClick={(evt) => {
            logEvent('Clicked Notifications Icon');
            setAnchorEl(isOpen ? null : evt.target);
          }}
        >
          <PreviewPopover
            open={isOpen}
            pollingNotifications={pollingNotifications}
            markPollingNotificationRead={markPollingNotificationRead}
            userRole={userTypes}
            markAllPollingNotificationsRead={markAllPollingNotificationsRead}
          >
            <FDIcon icon="notifications" color="primary" size="md" />
          </PreviewPopover>
        </MuiIconButton>
      </ClickAwayListener>
    </Badge>
  );
};

export const NotificationsNavButton = () => {
  const { userResource } = useUserContext();
  return userResource.withRelationship(fr.relationships.notifications.name,
    (_) => <NotificationsNavButtonContent />, () => null);
};
